import { useCallback, useRef } from 'react';
import * as Y from 'yjs';
import { deserializeIntoYjs, CANVAS_CLIPBOARD_MIME } from '@/features/canvas/engine/primitives/clipboard';
import { useCanvasBinding } from '@/features/canvas/engine/wiring/contexts/canvas-binding-context';
import { isDesktop } from '@/lib/platform';

export interface UsePasteHandlerArgs {
  doc: Y.Doc;
  clientId: number;
  getCursorPosition: () => { x: number; y: number };
  onResolverPaste: (text: string, position: { x: number; y: number }) => void | Promise<void>;
}

/**
 * Returns a ref-callback that, when attached to an element, installs a `paste`
 * listener delegating to either S2's canvas-clipboard deserializer or the
 * shell's resolver-paste handler. The engine seam is preserved: this hook
 * lives in shell/, consumes only S2's existing engine exports, and never
 * mutates engine/clipboard.ts.
 *
 * Paste delegation:
 *   1. `application/x-nula-canvas` MIME → call S2's deserializeIntoYjs
 *   2. `text/plain` non-empty → call onResolverPaste(text, cursorPosition)
 *   3. Otherwise: no-op (native fallthrough suppressed — canvas paste is resolver-driven)
 *
 * Implementation: the event handler is installed synchronously in the ref
 * callback so it fires on the same tick as the ref attachment. Stable refs
 * (docRef, getCursorRef, onResolverPasteRef) ensure the handler always sees
 * current values without needing to reinstall on every render.
 */
function isCanvasJson(text: string): boolean {
  if (!text.startsWith('{')) return false;
  try {
    const p = JSON.parse(text);
    return p !== null && typeof p === 'object' && 'nodes' in p;
  } catch {
    return false;
  }
}

// Exported for tests — the MIME constant is re-exported here so callers don't need to import clipboard.ts directly.
export { CANVAS_CLIPBOARD_MIME };

export function usePasteHandler({
  doc,
  clientId: _clientId,
  getCursorPosition,
  onResolverPaste,
}: UsePasteHandlerArgs) {
  // Keep stable refs for values the handler needs. This lets the returned
  // ref-callback remain stable (no deps change) while the handler always
  // sees the freshest doc/cursor/paste callbacks.
  const docRef = useRef(doc);
  docRef.current = doc;

  const binding = useCanvasBinding();
  const bindingRef = useRef(binding);
  bindingRef.current = binding;

  const getCursorRef = useRef(getCursorPosition);
  getCursorRef.current = getCursorPosition;

  const onResolverPasteRef = useRef(onResolverPaste);
  onResolverPasteRef.current = onResolverPaste;

  // Track the cleanup function for the currently-attached element.
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((node: HTMLElement | null) => {
    // Detach previous listener
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }

    if (!node) return;

    // Flag set by the paste handler to prevent the keydown fallback from
    // double-pasting when both fire (web browsers fire paste after keydown).
    let pasteHandledThisTick = false;

    const handler = (event: ClipboardEvent) => {
      // Ignore paste events originating from text inputs / contenteditable areas
      const target = event.target as HTMLElement | null;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        target?.isContentEditable ||
        target?.contentEditable === 'true'
      ) return;

      const cd = event.clipboardData;
      if (!cd) return;

      pasteHandledThisTick = true;

      if (Array.from(cd.types).includes('application/x-nula-canvas')) {
        const json = cd.getData('application/x-nula-canvas');
        if (json) {
          event.preventDefault();
          deserializeIntoYjs(json, docRef.current, getCursorRef.current(), undefined, bindingRef.current);
        }
        return;
      }

      const text = cd.getData('text/plain');
      if (text && text.length > 0) {
        event.preventDefault();
        // Canvas JSON serialized to text/plain (writeText fallback when ClipboardItem MIME is unsupported)
        if (isCanvasJson(text)) {
          deserializeIntoYjs(text, docRef.current, getCursorRef.current(), undefined, bindingRef.current);
        } else {
          void onResolverPasteRef.current(text, getCursorRef.current());
        }
      }
    };

    // Tauri (WKWebView) does not populate event.clipboardData when Cmd+V is
    // pressed on a non-editable element. Fall back to readText() on keydown.
    // On web, the paste event fires synchronously after keydown and sets
    // pasteHandledThisTick — the readText() promise resolves on the next
    // microtask tick, by which point the flag is already set.
    const keydownHandler = (event: KeyboardEvent) => {
      const isPaste = event.key === 'v' && (event.metaKey || event.ctrlKey);
      if (!isPaste) return;
      // Guard: if the event originated from an editable element (bubbled up),
      // event.target will be that element. Also check document.activeElement for
      // cases where the event is dispatched directly on document.
      const target = event.target as HTMLElement | null;
      const active = document.activeElement as HTMLElement | null;
      const editable = target ?? active;
      if (
        editable instanceof HTMLInputElement ||
        editable instanceof HTMLTextAreaElement ||
        editable instanceof HTMLSelectElement ||
        editable?.isContentEditable ||
        editable?.contentEditable === 'true'
      ) return;
      pasteHandledThisTick = false;
      // On Tauri desktop, use the clipboard-manager plugin which has direct OS
      // access and works reliably on both macOS and Windows without permission prompts.
      // On web, fall back to the Web Clipboard API.
      const readClipboard = isDesktop
        ? import('@tauri-apps/plugin-clipboard-manager').then(m => m.readText())
        : navigator.clipboard.readText();
      readClipboard.then((text) => {
        if (pasteHandledThisTick) return; // paste event already handled it
        if (!text) return;
        if (isCanvasJson(text)) {
          deserializeIntoYjs(text, docRef.current, getCursorRef.current(), undefined, bindingRef.current);
        } else {
          void onResolverPasteRef.current(text, getCursorRef.current());
        }
      }).catch(() => {});
    };

    // Listen on document so Cmd+V is captured regardless of which element has focus.
    // The canvas container div is not focusable, so the paste event never bubbles to it.
    document.addEventListener('paste', handler as EventListener);
    document.addEventListener('keydown', keydownHandler);
    cleanupRef.current = () => {
      document.removeEventListener('paste', handler as EventListener);
      document.removeEventListener('keydown', keydownHandler);
    };
  }, []); // Stable callback — no deps needed because refs always have fresh values
}
